import { api } from "./client";
import type { Balance, DailyRewardStatus, Hero, ItemHeroClass, ItemRarity, ItemSlot, PlayerItem, PremiumStatus } from "../types";

interface AdminActionResponse {
  ok: boolean;
  message: string;
  status: DailyRewardStatus;
}

export interface AdminHeroBoostResponse {
  ok: boolean;
  message: string;
  hero?: Hero;
  balance?: Balance;
  target_user_id?: number;
}

export interface AdminPlayerOption {
  user_id: number;
  label: string;
  hero_name: string | null;
  username: string | null;
  hero_class: ItemHeroClass | null;
  is_blocked: boolean;
}

export interface AdminItemTemplateOption {
  id: number;
  code: string;
  name: string;
  slot: ItemSlot;
  rarity: ItemRarity;
  hero_class: ItemHeroClass | null;
  item_level: number;
}

export interface AdminGrantItemResponse {
  ok: boolean;
  message: string;
  item: PlayerItem;
  target_user_id: number;
}

export interface AdminConsumableGrantOption {
  item_id: string;
  label: string;
  description: string | null;
}

export interface AdminGrantConsumableResponse {
  ok: boolean;
  message: string;
  target_user_id: number;
  item_id: string;
  quantity: number;
}

export interface AdminGemGrantOption {
  gem_type: string;
  level: number;
  label: string;
}

export interface AdminGrantGemResponse {
  ok: boolean;
  message: string;
  target_user_id: number;
  gem_type: string;
  level: number;
  quantity: number;
}

export interface AdminMaterialGrantOption {
  kind: string;
  level: number | null;
  label: string;
}

export interface AdminGrantMaterialResponse {
  ok: boolean;
  message: string;
  target_user_id: number;
  kind: string;
  level: number | null;
  amount: number;
}

export const adminApi = {
  advanceDailyDay: () => api.post<AdminActionResponse>("/admin/daily-reward/advance-day"),
  resetDailyCycle: () => api.post<AdminActionResponse>("/admin/daily-reward/reset-cycle"),
  simulateMissedDailyDay: () =>
    api.post<AdminActionResponse>("/admin/daily-reward/simulate-missed-day"),
  grantGold: (amount: number, targetUserId?: number | null) =>
    api.post<AdminHeroBoostResponse>("/admin/hero/grant-gold", {
      amount,
      target_user_id: targetUserId ?? null,
    }),
  grantDiamonds: (amount: number, targetUserId?: number | null) =>
    api.post<AdminHeroBoostResponse>("/admin/hero/grant-diamonds", {
      amount,
      target_user_id: targetUserId ?? null,
    }),
  grantStarDust: (amount: number, targetUserId?: number | null) =>
    api.post<AdminHeroBoostResponse>("/admin/hero/grant-star-dust", {
      amount,
      target_user_id: targetUserId ?? null,
    }),
  nearLevelUp: (gap = 10) =>
    api.post<AdminHeroBoostResponse>("/admin/hero/near-level-up", { gap }),
  searchPlayers: (q: string) =>
    api.get<{ players: AdminPlayerOption[] }>(`/admin/players/search?q=${encodeURIComponent(q)}`),
  searchItemTemplates: (q: string) =>
    api.get<{ templates: AdminItemTemplateOption[] }>(
      `/admin/item-templates?q=${encodeURIComponent(q)}`,
    ),
  grantGoldTo: (amount: number, targetUserId?: number | null) =>
    api.post<AdminHeroBoostResponse>("/admin/grants/gold", {
      amount,
      target_user_id: targetUserId ?? null,
    }),
  grantDiamondsTo: (amount: number, targetUserId?: number | null) =>
    api.post<AdminHeroBoostResponse>("/admin/grants/diamonds", {
      amount,
      target_user_id: targetUserId ?? null,
    }),
  grantStarDustTo: (amount: number, targetUserId?: number | null) =>
    api.post<AdminHeroBoostResponse>("/admin/grants/star-dust", {
      amount,
      target_user_id: targetUserId ?? null,
    }),
  grantItem: (templateId: number, targetUserId?: number | null, enhanceLevel = 0) =>
    api.post<AdminGrantItemResponse>("/admin/grants/item", {
      template_id: templateId,
      target_user_id: targetUserId ?? null,
      enhance_level: enhanceLevel,
    }),

  listGrantableConsumables: () =>
    api.get<AdminConsumableGrantOption[]>("/admin/grants/consumables"),

  grantConsumable: (itemId: string, quantity: number, targetUserId?: number | null) =>
    api.post<AdminGrantConsumableResponse>("/admin/grants/consumable", {
      item_id: itemId,
      quantity,
      target_user_id: targetUserId ?? null,
    }),

  listGrantableGems: () => api.get<AdminGemGrantOption[]>("/admin/grants/gems"),

  grantGem: (gemType: string, level: number, quantity: number, targetUserId?: number | null) =>
    api.post<AdminGrantGemResponse>("/admin/grants/gem", {
      gem_type: gemType,
      level,
      quantity,
      target_user_id: targetUserId ?? null,
    }),

  listGrantableCraftMaterials: () =>
    api.get<AdminMaterialGrantOption[]>("/admin/grants/craft-materials"),

  grantCraftMaterial: (
    kind: string,
    amount: number,
    level?: number | null,
    targetUserId?: number | null,
  ) =>
    api.post<AdminGrantMaterialResponse>("/admin/grants/craft-material", {
      kind,
      amount,
      level: level ?? null,
      target_user_id: targetUserId ?? null,
    }),

  resetCatacombs: (targetUserId?: number | null) =>
    api.post<{ ok: boolean; message: string; target_user_id: number; highest_cleared: number }>(
      "/admin/grants/catacombs-reset",
      { target_user_id: targetUserId ?? null },
    ),

  resetHero: (targetUserId?: number | null) =>
    api.post<AdminHeroBoostResponse & { target_user_id: number }>(
      "/admin/grants/hero-reset",
      { target_user_id: targetUserId ?? null },
    ),

  revokePremium: (opts: {
    targetUserId?: number | null;
    revokeVip: boolean;
    revokePro: boolean;
  }) =>
    api.post<{
      ok: boolean;
      message: string;
      target_user_id: number;
      premium: PremiumStatus;
      hero?: Hero;
    }>("/admin/premium/revoke", {
      target_user_id: opts.targetUserId ?? null,
      revoke_vip: opts.revokeVip,
      revoke_pro: opts.revokePro,
    }),

  resetWeeklyVipChest: (targetUserId?: number | null) =>
    api.post<{
      ok: boolean;
      message: string;
      target_user_id: number;
      previous_week: string | null;
      premium: PremiumStatus;
    }>("/admin/premium/reset-weekly-vip-chest", {
      target_user_id: targetUserId ?? null,
    }),

  respawnWorldBoss: () =>
    api.post<{ ok: boolean; message: string; boss_max_hp: number; boss_hp: number }>(
      "/admin/world-boss/respawn",
    ),

  openWorldBossWindow: (durationMinutes = 120) =>
    api.post<{ ok: boolean; message: string }>("/admin/world-boss/window/open", {
      duration_minutes: durationMinutes,
    }),

  closeWorldBossWindow: () =>
    api.post<{ ok: boolean; message: string }>("/admin/world-boss/window/close"),

  openMineEvent: (durationMinutes = 120) =>
    api.post<{ ok: boolean; message: string }>("/admin/mine/event/open", {
      duration_minutes: durationMinutes,
    }),

  closeMineEvent: () => api.post<{ ok: boolean; message: string }>("/admin/mine/event/close"),

  resetMineSlots: () => api.post<{ ok: boolean; message: string }>("/admin/mine/slots/reset"),

  guildBattleStatus: () =>
    api.get<{
      ok?: boolean;
      active: boolean;
      message?: string | null;
      week_key?: string;
      phase?: string;
      status?: string;
      phase_ends_at?: string;
      match_queue_len?: number;
      has_current_match?: boolean;
    }>("/admin/guild/battle/status"),

  resetGuildBattle: (reset_palaces = false) =>
    api.post<{ ok: boolean; message: string }>("/admin/guild/battle/reset", { reset_palaces }),

  startGuildBattle: () => api.post<{ ok: boolean; message: string }>("/admin/guild/battle/start"),

  advanceGuildBattle: () => api.post<{ ok: boolean; message: string }>("/admin/guild/battle/advance"),

  extendGuildCheckin: (minutes = 5) =>
    api.post<{ ok: boolean; message: string }>("/admin/guild/battle/extend-checkin", { minutes }),

  partyArenaStatus: () =>
    api.get<{
      ok?: boolean;
      active?: boolean;
      schedule_open?: boolean;
      admin_override?: boolean;
      admin_override_end?: string | null;
      matchmaking_queue_len?: number;
      waiting_rooms_count?: number;
      message?: string;
    }>("/admin/arena/party/status"),

  openPartyArena: (durationMinutes = 120) =>
    api.post<{ ok: boolean; message: string }>("/admin/arena/party/open", {
      duration_minutes: durationMinutes,
    }),

  closePartyArena: () => api.post<{ ok: boolean; message: string }>("/admin/arena/party/close"),

  removeSoloArenaPlayer: (targetUserId: number) =>
    api.post<{ ok: boolean; message: string; user_id: number; previous_rank: number }>(
      "/admin/arena/solo/remove-player",
      { target_user_id: targetUserId },
    ),

  resetSoloArenaDailyAttempts: () =>
    api.post<{ ok: boolean; message: string; players_reset: number; reset_date: string }>(
      "/admin/arena/solo/reset-daily-attempts",
      {},
    ),

  campaignBalanceSuggest: (body: {
    log: import("../types").BattleLogEvent[];
    enemies: import("../types").CampaignBattleEnemyRow[];
    battle_result: "WIN" | "LOSS";
    display_type: string;
    node_id?: string;
  }) =>
    api.post<{ ok: boolean; balance: import("../types").CampaignBattleBalancePayload }>(
      "/admin/campaign/balance/suggest",
      body,
    ),

  campaignBalanceApplyTemplates: (
    patches: Array<{
      code: string;
      hp?: number;
      atk?: number;
      def_?: number;
      spd?: number;
      fixed_damage?: number;
    }>,
  ) =>
    api.post<{ ok: boolean; message: string }>(
      "/admin/campaign/balance/apply-templates",
      { patches },
    ),

  campaignBalanceListMonsters: (tier?: number | null) => {
    const q = tier != null ? `?tier=${tier}` : "";
    return api.get<{ monsters: import("../types").CampaignBalanceMonsterOption[] }>(
      `/admin/campaign/balance/monsters${q}`,
    );
  },

  campaignBalanceApplyRun: (body: {
    run_id: number;
    node_id: string;
    enemies: import("../types").CampaignBalanceEnemySlotPayload[];
    template_patches?: Array<{
      code: string;
      hp?: number;
      atk?: number;
      def_?: number;
      spd?: number;
      fixed_damage?: number;
    }>;
    rollback_node?: boolean;
  }) =>
    api.post<{ ok: boolean; message: string; run: import("../types").CampaignRun }>(
      "/admin/campaign/balance/apply-run",
      body,
    ),

  getRouletteConfig: () =>
    api.get<{ ok: boolean; config: import("./roulette").RouletteAdminConfig }>("/admin/roulette/config"),

  saveRouletteConfig: (config: import("./roulette").RouletteAdminConfig) =>
    api.put<{ ok: boolean; message: string }>("/admin/roulette/config", config),

  activateRoulette: (durationMinutes = 120) =>
    api.post<{ ok: boolean; message: string }>("/admin/roulette/activate", {
      duration_minutes: durationMinutes,
    }),

  deactivateRoulette: () =>
    api.post<{ ok: boolean; message: string }>("/admin/roulette/deactivate"),
};
